import { useState } from 'react';
import { Bell, Info, CheckCircle2, AlertTriangle, XCircle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useNotifications, Notification } from '@/hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const NotificationIcon = ({ tipo }: { tipo: Notification['tipo'] }) => {
  switch (tipo) {
    case 'success': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'error': return <XCircle className="h-4 w-4 text-red-500" />;
    default: return <Info className="h-4 w-4 text-blue-500" />;
  }
};

const getBorderColor = (tipo: Notification['tipo']) => {
  switch (tipo) {
    case 'success': return 'border-l-green-500';
    case 'warning': return 'border-l-yellow-500';
    case 'error': return 'border-l-red-500';
    default: return 'border-l-blue-500';
  }
};

export const NotificationsPopover = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleNotificationClick = (n: Notification) => {
    if (!n.lida) markAsRead(n.id);
    if (n.link) {
      setOpen(false); // Fechar popover ao clicar
      navigate(n.link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-full" aria-label={`${unreadCount} notificações não lidas`}>
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground animate-in zoom-in">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="font-semibold">Notificações</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-auto p-0 text-xs text-muted-foreground hover:text-primary"
              onClick={markAllAsRead}
            >
              Marcar todas como lidas
            </Button>
          )}
        </div>
        <Separator />
        <ScrollArea className="h-[350px]">
          {notifications.length > 0 ? (
            <div className="flex flex-col">
              {notifications.map((n) => (
                <button 
                  key={n.id} 
                  onClick={() => handleNotificationClick(n)}
                  className={cn(
                    "flex flex-col gap-1 border-b border-l-4 p-4 text-left transition-colors hover:bg-muted/50 w-full",
                    getBorderColor(n.tipo),
                    !n.lida && "bg-primary/5"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <NotificationIcon tipo={n.tipo} />
                      <span className={cn("text-sm font-medium", !n.lida ? "text-foreground" : "text-muted-foreground")}>
                        {n.titulo}
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(n.criado_em), { addSuffix: true, locale: ptBR })}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 pl-6">
                    {n.mensagem}
                  </p>
                  {n.link && (
                    <div className="flex items-center gap-1 text-[10px] text-primary mt-1 pl-6">
                      <ExternalLink className="h-3 w-3" />
                      <span>Ver detalhes</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex h-[300px] flex-col items-center justify-center gap-2 text-muted-foreground">
              <Bell className="h-8 w-8 opacity-20" />
              <p className="text-sm">Nenhuma notificação por enquanto</p>
            </div>
          )}
        </ScrollArea>
        <Separator />
        <div className="p-2">
          <Button variant="ghost" size="sm" className="w-full text-xs">
            Ver histórico completo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
